<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { webOrdersAPI } from 'dashboard/api/webOrder';
import { ORDER_SOURCES } from 'dashboard/routes/dashboard/webOrder/helpers/orderFilters';
import {
  DATE_RANGE_TYPES,
  getActiveDateRange,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import DownloadReportForm from './components/download/DownloadReportForm.vue';
import DownloadReportPreview from './components/download/DownloadReportPreview.vue';
import {
  buildOriginCsv,
  originCsvFileName,
  originCsvMetricCells,
} from './helpers/downloadOriginCsv';
import {
  DEFAULT_CUSTOMER_RANK,
  DEFAULT_DATE_BASIS,
  DEFAULT_DOWNLOAD_REPORT_SUBTYPE,
  DEFAULT_DOWNLOAD_REPORT_TYPE,
  DEFAULT_PAYMENT_TYPE,
  DEFAULT_PRODUCT_GRAIN,
  DEFAULT_REPORT_LAYOUT,
  DEFAULT_REPORT_MODE,
  DEFAULT_UPSALE_MODE,
  DOWNLOAD_REPORT_PER_PAGE,
  ORIGIN_COUNT_COLUMNS,
  ORIGIN_RATE_COLUMNS,
  PAID_DUE_COLUMNS,
  DELIVERY_SHIPMENT_LEAD,
  DELIVERY_SHIPMENT_METRICS,
  customerLeadColumns,
  customerMetricColumns,
  formatLocalDateTimeKey,
  inventoryLeadColumns,
  inventoryMetricColumns,
  isDownloadPreviewAvailable,
  lockedDateBasis,
  resolvedCustomerRank,
  resolvedPaymentType,
  resolvedProductGrain,
  resolvedReportLayout,
  resolvedReportMode,
  resolvedUpsaleMode,
  showsCustomerRank,
  showsDateBasis,
  showsPaymentType,
  showsPermissionGroups,
  showsReportLayout,
  showsReportMode,
  showsSingleStockDate,
  showsOrderSources,
  showsPartnerFilter,
  showsProductFilter,
  showsProductGrain,
  showsStatusFilter,
  showsUpsaleMode,
  showsWarehouseFilter,
} from './helpers/downloadReportCatalog';
import {
  buildCsv,
  paidDueCsvFileName,
  paidDueCsvMetricCells,
} from './helpers/downloadPaidDueCsv';
import {
  buildCsv as buildProductionCsv,
  productionCsvFileName,
  productionCsvMetricCells,
} from './helpers/downloadProductionCsv';
import {
  buildCsv as buildCustomerCsv,
  customerCsvFileName,
  customerCsvLeadCells,
  customerCsvMetricCells,
} from './helpers/downloadCustomerCsv';
import {
  buildUserCsv,
  isUserDetailLayout,
  userCsvColumns,
  userCsvFileName,
  userCsvMetricCells,
  userDetailCsvCells,
  userDetailTotalsCells,
} from './helpers/downloadUserCsv';
import {
  buildCsv as buildInventoryCsv,
  inventoryCsvFileName,
  inventoryCsvLeadCells,
  inventoryCsvMetricCells,
} from './helpers/downloadInventoryCsv';
import {
  buildCsv as buildDeliveryCsv,
  deliveryCsvFileName,
  deliveryCsvLeadCells,
  deliveryCsvMetricCells,
} from './helpers/downloadDeliveryCsv';

const { t, te } = useI18n();

const last7Days = () =>
  getActiveDateRange(DATE_RANGE_TYPES.LAST_7_DAYS, new Date());

const todayDateKey = () => {
  const now = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const stockDayBounds = day => {
  const start = new Date(`${day}T00:00:00`);
  const end = new Date(`${day}T23:59:59`);
  return {
    from: formatLocalDateTimeKey(start),
    to: formatLocalDateTimeKey(end),
  };
};

const initialRange = last7Days();
const reportType = ref(DEFAULT_DOWNLOAD_REPORT_TYPE);
const reportSubtype = ref(DEFAULT_DOWNLOAD_REPORT_SUBTYPE);
const dateBasis = ref(DEFAULT_DATE_BASIS);
const paymentType = ref(DEFAULT_PAYMENT_TYPE);
const reportMode = ref(DEFAULT_REPORT_MODE);
const reportLayout = ref(DEFAULT_REPORT_LAYOUT);
const upsaleMode = ref(DEFAULT_UPSALE_MODE);
const customerRank = ref(DEFAULT_CUSTOMER_RANK);
const stockDate = ref(todayDateKey());
const dateRange = ref([initialRange.start, initialRange.end]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const orderSources = ref([]);
const statusFilters = ref([]);
const employeeIds = ref([]);
const customRoleIds = ref([]);
const warehouseId = ref(null);
const partnerIds = ref([]);
const productIds = ref([]);
const productGrain = ref(DEFAULT_PRODUCT_GRAIN);

const isLoading = ref(false);
const isExporting = ref(false);
const previewStatus = ref('idle');
const previewPayload = ref(null);
const previewError = ref('');
let previewRequestId = 0;

const filterSignature = computed(() =>
  JSON.stringify({
    reportType: reportType.value,
    reportSubtype: reportSubtype.value,
    dateBasis: dateBasis.value,
    paymentType: showsPaymentType(reportType.value, reportSubtype.value)
      ? resolvedPaymentType(paymentType.value)
      : '',
    reportMode: showsReportMode(reportType.value, reportSubtype.value)
      ? resolvedReportMode(reportMode.value)
      : '',
    upsaleMode: showsUpsaleMode(reportType.value, reportSubtype.value)
      ? resolvedUpsaleMode(upsaleMode.value)
      : '',
    reportLayout: showsReportLayout(reportType.value, reportSubtype.value)
      ? resolvedReportLayout(reportLayout.value)
      : '',
    customRoleIds: showsPermissionGroups(reportType.value, reportSubtype.value)
      ? [...customRoleIds.value].map(Number).sort((left, right) => left - right)
      : [],
    customerRank: showsCustomerRank(reportType.value, reportSubtype.value)
      ? resolvedCustomerRank(customerRank.value)
      : '',
    stockDate: stockDate.value,
    from: formatLocalDateTimeKey(dateRange.value?.[0]),
    to: formatLocalDateTimeKey(dateRange.value?.[1]),
    sources: [...orderSources.value].sort(),
    statuses: [...statusFilters.value].sort(),
    employeeIds: [...employeeIds.value]
      .map(Number)
      .sort((left, right) => left - right),
    warehouseId: warehouseId.value ? Number(warehouseId.value) : null,
    partnerIds: [...partnerIds.value]
      .map(Number)
      .sort((left, right) => left - right),
    productIds: showsProductFilter(reportType.value, reportSubtype.value)
      ? [...productIds.value].map(Number).sort((left, right) => left - right)
      : [],
    productGrain: showsProductGrain(reportType.value, reportSubtype.value)
      ? resolvedProductGrain(productGrain.value)
      : '',
  })
);

const canDownloadCsv = computed(
  () =>
    previewStatus.value === 'table' &&
    Boolean(previewPayload.value?.available) &&
    (previewPayload.value?.rows || []).length > 0
);

const resetPreview = () => {
  previewRequestId += 1;
  previewStatus.value = 'idle';
  previewPayload.value = null;
  previewError.value = '';
  isLoading.value = false;
};

const resetForm = () => {
  const range = last7Days();
  reportType.value = DEFAULT_DOWNLOAD_REPORT_TYPE;
  reportSubtype.value = DEFAULT_DOWNLOAD_REPORT_SUBTYPE;
  dateBasis.value = DEFAULT_DATE_BASIS;
  paymentType.value = DEFAULT_PAYMENT_TYPE;
  reportMode.value = DEFAULT_REPORT_MODE;
  reportLayout.value = DEFAULT_REPORT_LAYOUT;
  upsaleMode.value = DEFAULT_UPSALE_MODE;
  customerRank.value = DEFAULT_CUSTOMER_RANK;
  stockDate.value = todayDateKey();
  dateRange.value = [range.start, range.end];
  rangeType.value = DATE_RANGE_TYPES.LAST_7_DAYS;
  orderSources.value = [];
  statusFilters.value = [];
  employeeIds.value = [];
  customRoleIds.value = [];
  warehouseId.value = null;
  partnerIds.value = [];
  productIds.value = [];
  productGrain.value = DEFAULT_PRODUCT_GRAIN;
};

const buildPreviewParams = page => {
  const singleDay = showsSingleStockDate(reportType.value, reportSubtype.value);
  const bounds = singleDay
    ? stockDayBounds(stockDate.value || todayDateKey())
    : {
        from: formatLocalDateTimeKey(dateRange.value?.[0]),
        to: formatLocalDateTimeKey(dateRange.value?.[1]),
      };
  const params = {
    report_type: reportType.value,
    report_subtype: reportSubtype.value,
    from: bounds.from,
    to: bounds.to,
    page,
    per_page: DOWNLOAD_REPORT_PER_PAGE,
  };
  const locked = lockedDateBasis(reportType.value, reportSubtype.value);
  if (locked) {
    params.date_basis = locked;
  } else if (showsDateBasis(reportType.value, reportSubtype.value)) {
    params.date_basis = dateBasis.value;
  }
  if (
    showsOrderSources(reportType.value, reportSubtype.value) &&
    orderSources.value.length
  ) {
    params.order_sources = [...orderSources.value];
  }
  if (
    showsStatusFilter(reportType.value, reportSubtype.value) &&
    statusFilters.value.length
  ) {
    params.statuses = [...statusFilters.value];
  }
  if (reportType.value === 'user' && employeeIds.value.length) {
    params.agent_ids = employeeIds.value.map(Number);
  }
  if (showsReportLayout(reportType.value, reportSubtype.value)) {
    params.report_layout = resolvedReportLayout(reportLayout.value);
  }
  if (
    showsPermissionGroups(reportType.value, reportSubtype.value) &&
    customRoleIds.value.length
  ) {
    params.custom_role_ids = customRoleIds.value.map(Number);
  }
  if (
    showsWarehouseFilter(reportType.value, reportSubtype.value) &&
    warehouseId.value
  ) {
    params.warehouse_id = Number(warehouseId.value);
  }
  if (
    showsPartnerFilter(reportType.value, reportSubtype.value) &&
    partnerIds.value.length
  ) {
    params.partner_ids = partnerIds.value.map(Number);
  }
  if (
    showsProductFilter(reportType.value, reportSubtype.value) &&
    productIds.value.length
  ) {
    params.product_ids = productIds.value.map(Number);
  }
  if (showsProductGrain(reportType.value, reportSubtype.value)) {
    params.product_grain = resolvedProductGrain(productGrain.value);
  }
  if (showsPaymentType(reportType.value, reportSubtype.value)) {
    params.payment_type = resolvedPaymentType(paymentType.value);
  }
  if (showsReportMode(reportType.value, reportSubtype.value)) {
    params.report_mode = resolvedReportMode(reportMode.value);
  }
  if (showsUpsaleMode(reportType.value, reportSubtype.value)) {
    params.upsale_mode = resolvedUpsaleMode(upsaleMode.value);
  }
  if (showsCustomerRank(reportType.value, reportSubtype.value)) {
    params.customer_rank = resolvedCustomerRank(customerRank.value);
  }
  return params;
};

const originLabel = (origin, mode) => {
  if (mode === 'attribution') return origin || '';
  const source = String(origin || '').toUpperCase();
  const key = `WEB_ORDER.FILTERS.ORDER_SOURCES.${source}`;
  return te(key) ? t(key) : origin || '';
};

const originCsvHeaders = () => {
  const headers = [
    t('DOWNLOAD_REPORTS.TABLE.ORIGIN'),
    t('DOWNLOAD_REPORTS.TABLE.ORDERS'),
    t('DOWNLOAD_REPORTS.TABLE.AMOUNT'),
  ];
  ORIGIN_COUNT_COLUMNS.forEach(column => {
    const label = t(`DOWNLOAD_REPORTS.TABLE.${column.toUpperCase()}`);
    headers.push(label);
    headers.push(`${label} ${t('DOWNLOAD_REPORTS.TABLE.AMOUNT')}`);
  });
  ORIGIN_RATE_COLUMNS.forEach(column => {
    headers.push(t(`DOWNLOAD_REPORTS.TABLE.${column.toUpperCase()}`));
  });
  return headers;
};

const csvFromPayload = payload => {
  const rows = (payload.rows || []).map(row => [
    originLabel(row.origin, payload.report_mode),
    ...originCsvMetricCells(row),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    ...originCsvMetricCells(payload.totals || {}),
  ];
  return buildOriginCsv({ headers: originCsvHeaders(), rows, totals });
};

const paidDueColumnLabel = column => {
  if (column === 'total') return t('DOWNLOAD_REPORTS.TABLE.ORDER_TOTAL');
  return t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);
};

const paymentLabel = payment =>
  t(`DOWNLOAD_REPORTS.TABLE.PAYMENT_${String(payment || '').toUpperCase()}`);

const paidDueCsvFromPayload = payload => {
  const rows = (payload.rows || []).map(row => [
    paymentLabel(row.payment),
    ...paidDueCsvMetricCells(row),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    ...paidDueCsvMetricCells(payload.totals || {}),
  ];
  return buildCsv({
    headers: [
      t('DOWNLOAD_REPORTS.TABLE.PAYMENT'),
      ...PAID_DUE_COLUMNS.map(paidDueColumnLabel),
    ],
    rows,
    totals,
  });
};

const USER_COLUMN_LABELS = {
  invoice_number: 'INVOICE',
  agent_name: 'AGENT',
};

const userTableHeader = column => {
  const key = USER_COLUMN_LABELS[column] || String(column).toUpperCase();
  return t(`DOWNLOAD_REPORTS.TABLE.${key}`);
};

const userCsvHeaders = subtype => [
  t('DOWNLOAD_REPORTS.TABLE.AGENT'),
  ...userCsvColumns(subtype).map(userTableHeader),
];

const productionDateHeader = payload => {
  const basis = payload?.date_basis;
  if (basis === 'approved')
    return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE_APPROVAL');
  if (basis === 'creation')
    return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE_CREATION');
  if (basis === 'in_transit') {
    return t('DOWNLOAD_REPORTS.FORM.DATE_BASIS_OPTIONS.IN_TRANSIT');
  }
  if (basis === 'delivered') {
    return t('DOWNLOAD_REPORTS.FORM.DATE_BASIS_OPTIONS.DELIVERED');
  }
  return t('DOWNLOAD_REPORTS.TABLE.DATE');
};

const productionCsvHeaders = payload => [
  productionDateHeader(payload),
  t('DOWNLOAD_REPORTS.TABLE.SKU'),
  t('DOWNLOAD_REPORTS.TABLE.PRODUCT'),
  t('DOWNLOAD_REPORTS.TABLE.ORDERS'),
  t('DOWNLOAD_REPORTS.TABLE.QTY'),
  t('DOWNLOAD_REPORTS.TABLE.AMOUNT'),
];

const customerColumnLabel = (column, subtype) => {
  if (column === 'customer_name') return t('DOWNLOAD_REPORTS.TABLE.CUSTOMER');
  if (column === 'product_name') return t('DOWNLOAD_REPORTS.TABLE.PRODUCT');
  if (subtype === 'top_customer_repeat' && column === 'orders') {
    return t('DOWNLOAD_REPORTS.TABLE.ORDER_FREQUENCY');
  }
  if (subtype === 'top_customer_repeat' && column === 'amount') {
    return t('DOWNLOAD_REPORTS.TABLE.ORDER_VALUE');
  }
  return t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);
};

const customerCsvFromPayload = payload => {
  const subtype = payload.report_subtype;
  const lead = customerLeadColumns(subtype);
  const rows = (payload.rows || []).map(row => [
    ...customerCsvLeadCells(row, subtype),
    ...customerCsvMetricCells(row, subtype),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    ...lead.slice(1).map(() => ''),
    ...customerCsvMetricCells(payload.totals || {}, subtype),
  ];
  return buildCustomerCsv({
    headers: [
      ...lead.map(column => customerColumnLabel(column, subtype)),
      ...customerMetricColumns(subtype).map(column =>
        customerColumnLabel(column, subtype)
      ),
    ],
    rows,
    totals,
  });
};

const orderSourceLabel = source => {
  const key = `WEB_ORDER.FILTERS.ORDER_SOURCES.${String(source).toUpperCase()}`;
  return te(key) ? t(key) : source;
};

const dailyProductCsvFromPayload = payload => {
  const totals = payload.totals || {};
  const sourceCells = row =>
    ORDER_SOURCES.map(source => row?.sources?.[source] || 0);
  const rows = (payload.rows || []).map(row => [
    row.sale_date || '',
    row.sku || '',
    row.product_name || '',
    ...sourceCells(row),
    row.qty || 0,
    row.product_price ?? '',
    row.discount ?? '',
  ]);
  return buildProductionCsv({
    headers: [
      productionDateHeader(payload),
      t('DOWNLOAD_REPORTS.TABLE.SKU'),
      t('DOWNLOAD_REPORTS.TABLE.PRODUCT'),
      ...ORDER_SOURCES.map(orderSourceLabel),
      t('DOWNLOAD_REPORTS.TABLE.TOTAL_PRODUCT_QUANTITY'),
      t('DOWNLOAD_REPORTS.TABLE.PRODUCT_PRICE'),
      t('DOWNLOAD_REPORTS.TABLE.TOTAL_PRODUCT_DISCOUNT'),
    ],
    rows,
    totals: [
      t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
      '',
      '',
      ...sourceCells(totals),
      totals.qty || 0,
      '',
      totals.discount ?? '',
    ],
  });
};

const productionCsvFromPayload = payload => {
  if (payload.report_subtype === 'daily_product_wise_sales') {
    return dailyProductCsvFromPayload(payload);
  }
  const rows = (payload.rows || []).map(row => [
    row.sale_date || '',
    row.sku || '',
    row.product_name || '',
    ...productionCsvMetricCells(row),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    '',
    '',
    ...productionCsvMetricCells(payload.totals || {}),
  ];
  return buildProductionCsv({
    headers: productionCsvHeaders(payload),
    rows,
    totals,
  });
};

const inventoryColumnLabel = column => {
  if (column === 'product_name') return t('DOWNLOAD_REPORTS.TABLE.PRODUCT');
  if (column === 'expires_on') return t('DOWNLOAD_REPORTS.TABLE.EXPIRES');
  if (column === 'sale_date') return t('DOWNLOAD_REPORTS.TABLE.DATE');
  if (column === 'category') return t('DOWNLOAD_REPORTS.TABLE.CATEGORY');
  return t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);
};

const inventoryCsvFromPayload = payload => {
  const subtype = payload.report_subtype;
  const lead = inventoryLeadColumns(subtype);
  const rows = (payload.rows || []).map(row => [
    ...inventoryCsvLeadCells(row, subtype),
    ...inventoryCsvMetricCells(row, subtype),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    ...lead.slice(1).map(() => ''),
    ...inventoryCsvMetricCells(payload.totals || {}, subtype),
  ];
  return buildInventoryCsv({
    headers: [
      ...lead.map(inventoryColumnLabel),
      ...inventoryMetricColumns(subtype).map(inventoryColumnLabel),
    ],
    rows,
    totals,
  });
};

const deliveryColumnLabel = column => {
  if (column === 'partner_name') return t('DOWNLOAD_REPORTS.TABLE.PARTNER');
  if (column === 'cancelled') {
    return t('DOWNLOAD_REPORTS.TABLE.CANCELLED_RETURNED');
  }
  return t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);
};

const deliveryCsvFromPayload = payload => {
  const rows = (payload.rows || []).map(row => [
    ...deliveryCsvLeadCells(row),
    ...deliveryCsvMetricCells(row),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    ...DELIVERY_SHIPMENT_LEAD.slice(1).map(() => ''),
    ...deliveryCsvMetricCells(payload.totals || {}),
  ];
  return buildDeliveryCsv({
    headers: [...DELIVERY_SHIPMENT_LEAD, ...DELIVERY_SHIPMENT_METRICS].map(
      deliveryColumnLabel
    ),
    rows,
    totals,
  });
};

const userCsvFromPayload = payload => {
  const subtype = payload.report_subtype;
  const layout = payload.report_layout;
  const columns = userCsvColumns(subtype, layout);
  if (isUserDetailLayout(subtype, layout)) {
    const totals = userDetailTotalsCells(payload.totals || {}, columns);
    totals[0] = t('DOWNLOAD_REPORTS.TABLE.TOTALS');
    return buildUserCsv({
      headers: columns.map(userTableHeader),
      rows: (payload.rows || []).map(row => userDetailCsvCells(row, columns)),
      totals,
    });
  }
  const rows = (payload.rows || []).map(row => [
    row.agent_name || '',
    ...userCsvMetricCells(row, columns),
  ]);
  const totals = [
    t('DOWNLOAD_REPORTS.TABLE.TOTALS'),
    ...userCsvMetricCells(payload.totals || {}, columns),
  ];
  return buildUserCsv({
    headers: userCsvHeaders(subtype),
    rows,
    totals,
  });
};

const fetchPreview = async page => {
  const requestId = previewRequestId + 1;
  previewRequestId = requestId;
  isLoading.value = true;
  previewError.value = '';
  try {
    const { data } = await webOrdersAPI.downloadReportPreview(
      buildPreviewParams(page)
    );
    if (requestId !== previewRequestId) return;
    const payload = data?.payload ?? data;
    const withLocalRange = current => {
      if (
        !current ||
        current.meta?.range_clamped ||
        current.report_type === 'production' ||
        current.report_type === 'customer' ||
        current.report_type === 'inventory' ||
        current.report_type === 'delivery_partner'
      ) {
        return current;
      }
      return {
        ...current,
        from: formatLocalDateTimeKey(dateRange.value?.[0]),
        to: formatLocalDateTimeKey(dateRange.value?.[1]),
      };
    };
    if (!payload?.available) {
      previewPayload.value = withLocalRange(payload || null);
      previewStatus.value = 'coming_soon';
      return;
    }
    previewPayload.value = withLocalRange(payload);
    previewStatus.value = (payload.rows || []).length ? 'table' : 'empty';
  } catch (error) {
    if (requestId !== previewRequestId) return;
    previewPayload.value = null;
    previewStatus.value = 'error';
    previewError.value =
      error?.response?.status === 429
        ? t('DOWNLOAD_REPORTS.PREVIEW.RATE_LIMIT')
        : t('DOWNLOAD_REPORTS.PREVIEW.ERROR');
  } finally {
    if (requestId === previewRequestId) {
      isLoading.value = false;
    }
  }
};

const csvBounds = payload => {
  if (payload?.meta?.range_clamped) {
    return { from: payload.from, to: payload.to };
  }
  return {
    from: formatLocalDateTimeKey(dateRange.value?.[0]),
    to: formatLocalDateTimeKey(dateRange.value?.[1]),
  };
};

const onPreview = () => {
  if (!isDownloadPreviewAvailable(reportType.value, reportSubtype.value)) {
    previewPayload.value = null;
    previewError.value = '';
    previewStatus.value = 'coming_soon';
    return;
  }
  fetchPreview(1);
};

const onClear = () => {
  resetForm();
  resetPreview();
};

const saveCsv = payload => {
  const { from, to } = csvBounds(payload);
  if (payload.report_type === 'inventory') {
    downloadCsvFile(
      inventoryCsvFileName(payload.report_subtype, payload.from, payload.to),
      inventoryCsvFromPayload(payload)
    );
    return;
  }
  if (payload.report_type === 'delivery_partner') {
    downloadCsvFile(
      deliveryCsvFileName(payload.from, payload.to),
      deliveryCsvFromPayload(payload)
    );
    return;
  }
  if (payload.report_type === 'customer') {
    downloadCsvFile(
      customerCsvFileName(payload.report_subtype, payload.from, payload.to),
      customerCsvFromPayload(payload)
    );
    return;
  }
  if (payload.report_type === 'production') {
    downloadCsvFile(
      productionCsvFileName(payload.report_subtype, payload.from, payload.to),
      productionCsvFromPayload(payload)
    );
    return;
  }
  if (payload.report_type === 'user') {
    downloadCsvFile(
      userCsvFileName(payload.report_subtype, from, to),
      userCsvFromPayload(payload)
    );
    return;
  }
  if (payload.report_subtype === 'paid_and_due_amount') {
    downloadCsvFile(
      paidDueCsvFileName(from, to),
      paidDueCsvFromPayload(payload)
    );
    return;
  }
  downloadCsvFile(originCsvFileName(from, to), csvFromPayload(payload));
};

const onDownload = async () => {
  if (!canDownloadCsv.value || isLoading.value || isExporting.value) return;
  isExporting.value = true;
  try {
    const { data } = await webOrdersAPI.downloadReportPreview({
      ...buildPreviewParams(1),
      export: 1,
    });
    const payload = data?.payload ?? data;
    if (!payload?.available || !(payload.rows || []).length) return;
    saveCsv(payload);
  } catch (error) {
    previewPayload.value = null;
    previewStatus.value = 'error';
    previewError.value =
      error?.response?.status === 429
        ? t('DOWNLOAD_REPORTS.PREVIEW.RATE_LIMIT')
        : t('DOWNLOAD_REPORTS.PREVIEW.ERROR');
  } finally {
    isExporting.value = false;
  }
};

const onPageChange = page => {
  if (!isDownloadPreviewAvailable(reportType.value, reportSubtype.value)) {
    return;
  }
  fetchPreview(page);
};

watch(filterSignature, () => {
  resetPreview();
});
</script>

<template>
  <ReportHeader
    :header-title="t('DOWNLOAD_REPORTS.HEADER')"
    :header-description="t('DOWNLOAD_REPORTS.DESCRIPTION')"
  />
  <div class="flex flex-col gap-4 pb-6">
    <DownloadReportForm
      v-model:report-type="reportType"
      v-model:report-subtype="reportSubtype"
      v-model:date-basis="dateBasis"
      v-model:payment-type="paymentType"
      v-model:report-mode="reportMode"
      v-model:report-layout="reportLayout"
      v-model:upsale-mode="upsaleMode"
      v-model:customer-rank="customerRank"
      v-model:stock-date="stockDate"
      v-model:date-range="dateRange"
      v-model:range-type="rangeType"
      v-model:order-sources="orderSources"
      v-model:status-filters="statusFilters"
      v-model:employee-ids="employeeIds"
      v-model:custom-role-ids="customRoleIds"
      v-model:warehouse-id="warehouseId"
      v-model:partner-ids="partnerIds"
      v-model:product-ids="productIds"
      v-model:product-grain="productGrain"
      :is-loading="isLoading"
      :is-exporting="isExporting"
      :can-download-csv="canDownloadCsv"
      @preview="onPreview"
      @clear="onClear"
      @download="onDownload"
    />
    <DownloadReportPreview
      :is-loading="isLoading"
      :status="previewStatus"
      :payload="previewPayload"
      :error-message="previewError"
      :report-type="reportType"
      :report-subtype="reportSubtype"
      :filters="{
        sources: orderSources.length > 0,
        statuses: statusFilters.length > 0,
        employees: employeeIds.length > 0,
        warehouse: Boolean(warehouseId),
        partners: partnerIds.length > 0,
      }"
      @page-change="onPageChange"
    />
  </div>
</template>
